/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/performances-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-performances-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/performances-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, round_id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/performances-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-performances-api-client.ts (API client)
 * - client/api/generated_schemas/performances.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-09-15T20:50:53.151Z
 * Source config: config/entities/performances-entity-config.ts
 * Public fields: 11/23 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PerformancesClient, PerformancesQueryOptions } from '../generated_clients/generated-performances-api-client.js';
import {
  PerformancesInsert,
  PerformancesUpdate,
  PerformancesFieldConfig
} from '../../types/generated_schemas/generated-performances-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/performances_business_hooks/

// Query keys with enhanced caching strategy
export const performancesKeys = {
  all: ['performances'] as const,
  lists: () => [...performancesKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...performancesKeys.lists(), { filters }] as const,
  details: () => [...performancesKeys.all, 'detail'] as const,
  detail: (id: string) => [...performancesKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...performancesKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...performancesKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...performancesKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...performancesKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const usePerformancesClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new PerformancesClient();
};

/**
 * Hook to get all performances with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with performances data
 */
export const usePerformancesList = (options?: PerformancesQueryOptions) => {
  const client = usePerformancesClient();
  
  return useQuery({
    queryKey: performancesKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single performance by ID
 *
 * @param id - The performance ID
 * @returns React Query result with single performance data
 */
export const usePerformances = (id: string) => {
  const client = usePerformancesClient();
  
  return useQuery({
    queryKey: performancesKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/performances
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/performances_business_hooks/
 */

/**
 * Hook to create a new performance
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating performance
 */
export const useCreatePerformances = () => {
  const client = usePerformancesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: PerformancesInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: performancesKeys.all });
    },
  });
};

/**
 * Hook to update an existing performance
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating performance
 */
export const useUpdatePerformances = () => {
  const client = usePerformancesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PerformancesUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: performancesKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: performancesKeys.lists() });
    },
  });
};

/**
 * Hook to delete a performance
 *
 * @returns React Query mutation for deleting performance
 */
export const useDeletePerformances = () => {
  const client = usePerformancesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: performancesKeys.all });
    },
  });
};

/**
 * Hook to count performances with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, round_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const usePerformancesCount = (filter?: Partial<Record<typeof PerformancesFieldConfig.filterable[number], any>>) => {
  const client = usePerformancesClient();
  
  return useQuery({
    queryKey: performancesKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const usePerformancesFieldConfig = () => {
  return PerformancesFieldConfig;
};